import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  RadioGroup,
  NativeSelect,
  Spinner,
  Stack,
  Text,
  Textarea,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Radio } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { repertorioCompartidoAPI } from '../services/api';

const TIPOS = [
  { value: 'OBRA_NO_APARECE', label: 'No aparece la obra' },
  { value: 'VOZ_INCORRECTA', label: 'Mi voz no es la correcta' },
  { value: 'OTRO', label: 'Otro' },
];

function formatMusicoNombre(musico) {
  if (!musico) return '';
  return [musico.nombre, musico.apellidos].filter(Boolean).join(' ').trim();
}

export default function RepertorioIncidenciaReportModal({
  isOpen,
  onClose,
  token,
  accessToken,
  carpetas,
  initialCarpetaId,
  selectedMusico,
  asignaciones,
  selectedAsignacionId,
  partituraInstrumentSlug,
  partituraVozEtiquetasCsv,
}) {
  
  const [carpetaId, setCarpetaId] = useState('');
  const [obraId, setObraId] = useState('');
  const [instrumentoSlug, setInstrumentoSlug] = useState('');
  const [instrumentoNombre, setInstrumentoNombre] = useState('');
  const [vozEtiquetas, setVozEtiquetas] = useState('');
  const [vozDisplay, setVozDisplay] = useState('');
  const [tipo, setTipo] = useState('OBRA_NO_APARECE');
  const [descripcion, setDescripcion] = useState('');
  const [obrasOpciones, setObrasOpciones] = useState([]);
  const [obrasLoading, setObrasLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [asignacionPickId, setAsignacionPickId] = useState('');

  const asignacionActual = useMemo(() => {
    const id = asignacionPickId || selectedAsignacionId;
    if (!id) return asignaciones[0] || null;
    return asignaciones.find((a) => a.id === id) || asignaciones[0] || null;
  }, [asignaciones, selectedAsignacionId, asignacionPickId]);

  const resetForm = useCallback(() => {
    const cid = initialCarpetaId || carpetas[0]?.id || '';
    setCarpetaId(cid);
    setObraId('');
    setTipo('OBRA_NO_APARECE');
    setDescripcion('');
    const slug = partituraInstrumentSlug || asignacionActual?.instrumentSlug || asignacionActual?.etiqueta || '';
    setInstrumentoSlug(slug || '');
    setInstrumentoNombre(asignacionActual?.displayName?.split(' · ')[0]?.trim() || '');
    const vozCsv =
      partituraVozEtiquetasCsv ||
      (Array.isArray(asignacionActual?.vozEtiquetas) && asignacionActual.vozEtiquetas.length
        ? asignacionActual.vozEtiquetas.join(',')
        : '');
    setVozEtiquetas(vozCsv || '');
    const vozNom = asignacionActual?.displayName?.includes(' · ')
      ? asignacionActual.displayName.split(' · ').slice(1).join(' · ').trim()
      : '';
    setVozDisplay(vozNom || '');
    setAsignacionPickId(selectedAsignacionId || asignaciones[0]?.id || '');
  }, [
    initialCarpetaId,
    carpetas,
    partituraInstrumentSlug,
    partituraVozEtiquetasCsv,
    asignacionActual,
    selectedAsignacionId,
    asignaciones,
  ]);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen || !token || !accessToken || !carpetaId) {
      setObrasOpciones([]);
      return undefined;
    }
    let cancelled = false;
    setObrasLoading(true);
    repertorioCompartidoAPI
      .getObrasEnCarpeta(token, accessToken, carpetaId, {})
      .then((res) => {
        if (!cancelled) setObrasOpciones(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setObrasOpciones([]);
      })
      .finally(() => {
        if (!cancelled) setObrasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, accessToken, carpetaId]);

  const obraSeleccionada = obrasOpciones.find((o) => o.id === obraId);

  const onSubmit = async () => {
    if (!token || !accessToken || !carpetaId) return;
    if (tipo === 'OTRO' && !descripcion.trim()) {
      toaster.create({
        title: 'Describe la incidencia',
        description: 'En el tipo «Otro» debes indicar qué ocurre.',
        type: 'warning',
      });
      return;
    }
    setSubmitting(true);
    try {
      const musicoNombre = formatMusicoNombre(selectedMusico);
      await repertorioCompartidoAPI.reportarIncidencia(token, accessToken, {
        carpetaId,
        obraId: obraId || undefined,
        obraTitulo: obraSeleccionada?.titulo || undefined,
        instrumentoSlug: instrumentoSlug || undefined,
        instrumentoNombre: instrumentoNombre || undefined,
        vozEtiquetas: vozEtiquetas || undefined,
        vozEtiquetasDisplay: vozDisplay || undefined,
        tipo,
        descripcion: tipo === 'OTRO' ? descripcion.trim() : undefined,
        musicoId: selectedMusico?.id || undefined,
        musicoNombre: musicoNombre || undefined,
      });
      toaster.create({
        title: 'Incidencia enviada',
        description: 'La banda la revisará desde su panel de gestión.',
        type: 'success',
        duration: 5000,
      });
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo enviar',
        description: Array.isArray(msg) ? msg.join('. ') : msg || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Reportar incidencia</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="gray.600">
                  Indica los datos de la incidencia. La banda recibirá una notificación y podrá marcarla como resuelta.
                </Text>
                <Field.Root required>
                  <Field.Label fontSize="sm">Carpeta</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      size="sm"
                      value={carpetaId}
                      onChange={(e) => {
                        setCarpetaId(e.target.value);
                        setObraId('');
                      }}>
                      <option value="">— Elige carpeta —</option>
                      {carpetas.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Obra (opcional)</Field.Label>
                  {obrasLoading ? (
                    <Spinner size="sm" color="purple.500" />
                  ) : (
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={obraId}
                        onChange={(e) => setObraId(e.target.value)}
                        disabled={!carpetaId}>
                        <option value="">— Sin especificar —</option>
                        {obrasOpciones.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.titulo}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  )}
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Instrumento</Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      size="sm"
                      value={asignacionPickId || ''}
                      onChange={(e) => {
                        setAsignacionPickId(e.target.value);
                        const a = asignaciones.find((x) => x.id === e.target.value);
                        if (a) {
                          setInstrumentoSlug(a.instrumentSlug || a.etiqueta || '');
                          setInstrumentoNombre(a.displayName?.split(' · ')[0]?.trim() || '');
                          setVozEtiquetas(
                            Array.isArray(a.vozEtiquetas) && a.vozEtiquetas.length ? a.vozEtiquetas.join(',') : '',
                          );
                          setVozDisplay(
                            a.displayName?.includes(' · ')
                              ? a.displayName.split(' · ').slice(1).join(' · ').trim()
                              : '',
                          );
                        }
                      }}
                      disabled={asignaciones.length === 0}>
                      {asignaciones.length === 0 ? (
                        <option value="">—</option>
                      ) : (
                        asignaciones.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.displayName}
                          </option>
                        ))
                      )}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  {asignaciones.length === 0 ? (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Sin asignación de instrumento en tu ficha; la banda verá el reporte sin filtro de voz.
                    </Text>
                  ) : null}
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Voz</Field.Label>
                  <Text fontSize="sm" color="gray.700">
                    {vozDisplay?.trim() ? vozDisplay : '—'}
                  </Text>
                </Field.Root>
                <Field.Root required>
                  <Field.Label fontSize="sm">Tipo de incidencia</Field.Label>
                  <RadioGroup.Root value={tipo} onValueChange={(d) => setTipo(d.value)}>
                    <Stack gap={2}>
                      {TIPOS.map((t) => (
                        <Radio key={t.value} value={t.value} size="sm">
                          {t.label}
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup.Root>
                </Field.Root>
                {tipo === 'OTRO' ? (
                  <Field.Root required>
                    <Field.Label fontSize="sm">Descripción</Field.Label>
                    <Textarea
                      size="sm"
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      placeholder="Explica qué ocurre…"
                      rows={4}
                      maxLength={2000}
                    />
                  </Field.Root>
                ) : null}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button colorPalette="purple" onClick={() => void onSubmit()} loading={submitting} disabled={!carpetaId}>
                Enviar incidencia
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
